import { computed } from 'vue'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

export interface UseActionValueFormat {
  /** A payload value rendered for its field (`daily_budget` as money, `target_status` as copy). */
  formatValue: (field: string, value: unknown) => string
  /** `guardrails.metrics.delta_pct` (already percent-scaled by the backend, e.g. `20.5`). */
  formatDelta: (deltaPct: number | null) => string
  formatTimestamp: (value: string | null) => string
}

/**
 * One place that renders platform-action payload values, so the preview diff, the table and the
 * detail drawer never disagree — and so components stay clear of raw `Intl` (a lint error there).
 * Money follows the workspace currency; everything else funnels through `useFormat` too.
 */
export function useActionValueFormat(): UseActionValueFormat {
  const workspace = useWorkspace()
  const { t, te } = useI18n()

  const format = computed(() => {
    const currency = workspace.currency.value
    return useFormat(currency ? { currency } : {})
  })

  function formatValue(field: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return t('platformActions.preview.noValue')
    }
    if (field === 'daily_budget' && typeof value === 'number') {
      return format.value.formatCurrency(value)
    }
    if (field === 'target_status' && typeof value === 'string') {
      const key = `platformActions.targetStatus.${value}`
      return te(key) ? t(key) : value
    }
    if (typeof value === 'number') return format.value.formatNumber(value)
    if (typeof value === 'string') return value
    return JSON.stringify(value)
  }

  return {
    formatValue,
    // The backend sends a percent-scaled number, not a 0–1 ratio.
    formatDelta: (deltaPct) =>
      deltaPct === null ? '' : format.value.formatPercent(deltaPct, { scale: 'percent' }),
    formatTimestamp: (value) =>
      value ? format.value.formatDateTime(value) : t('platformActions.preview.noValue'),
  }
}
